import { throttle } from 'lodash';
import type { RealtimeCacheTag } from './realtimeEventTags';

/**
 * The cache tags that events make stale at most one time in the given milliseconds. A render sends
 * `reel.updated` or `kit.asset.updated` up to one time each second (its progress), and each read of
 * these tags is a full answer of the server. The screens of the tag still feel live: the first
 * event reads at once, and more events in the time make one read at its end.
 */
export const THROTTLED_REALTIME_TAGS: Readonly<Record<string, number>> = {
  // The clean-copies run (`GET /accounts/:accountId/clean-copies`): the menu badge reads it on every page.
  CleanCopies: 3000,
};

export interface ThrottledTagInvalidation {
  /** Invalidates the tags of one event: a throttled tag by its own clock, the other tags at once. */
  invalidate: (tags: RealtimeCacheTag[]) => void;
  /** Drops the reads that still wait. Call it when the socket ends. */
  cancel: () => void;
}

/**
 * Splits the tags of an event (`realtimeEventTags`) into the tags that go to the cache at once and
 * the tags of `THROTTLED_REALTIME_TAGS`. Only a whole tag type is throttled, never one entry of a type.
 */
export function createThrottledTagInvalidation(send: (tags: RealtimeCacheTag[]) => void): ThrottledTagInvalidation {
  const throttled = new Map<string, ReturnType<typeof throttle>>();
  const throttleOf = (tag: string, waitMs: number) => {
    let run = throttled.get(tag);
    if (run === undefined) {
      run = throttle(() => send([tag]), waitMs, { leading: true, trailing: true });
      throttled.set(tag, run);
    }
    return run;
  };
  return {
    invalidate: (tags) => {
      const now: RealtimeCacheTag[] = [];
      const later: Array<[string, number]> = [];
      tags.forEach((tag) => {
        const waitMs = typeof tag === 'string' ? THROTTLED_REALTIME_TAGS[tag] : undefined;
        if (typeof tag === 'string' && waitMs !== undefined) {
          later.push([tag, waitMs]);
        } else {
          now.push(tag);
        }
      });
      if (now.length > 0) {
        send(now);
      }
      later.forEach(([tag, waitMs]) => throttleOf(tag, waitMs)());
    },
    cancel: () => {
      throttled.forEach((run) => run.cancel());
      throttled.clear();
    },
  };
}
